import React, { memo, Fragment } from 'react';
import {
  PrintSingleTaskConsumer,
  IPrintSingleTaskContext,
} from 'store/context/PrintSingleTaskContext';
import { ITaskPrintTemplateObject } from 'store/entity/IPrintTemplatePart';

import TextPart from './PrintParts/TextPart';
import DatePart from './PrintParts/DatePart';
import WeightPart from './PrintParts/WeightPart';
import FieldPart from './PrintParts/FieldsPart';
import SubTasksPart from './PrintParts/SubTasksPart';
import SignaturePart from './PrintParts/SignaturesPart';
import AssignmentPart from './PrintParts/AssignmentPart';
import ProjectNamePart from './PrintParts/ProjectNamePart';
import MainProgressPart from './PrintParts/MainProgressPart';
import SubTasksTitlePart from './PrintParts/SubTasksTitlePart';
import ProviderItemNamePart from './PrintParts/ProviderItemNamePart';
import {
  BreakPart,
  // PrintPage,
  SplitLine,
  PrintHeader,
  TD,
  IPrintTaskHeaderProps,
  PrintSingleTaskHeader,
  PrintSingleTaskHeaderWrapper,
  PrintSingleTaskContainer,
  LogoImage,
} from './styles';
import DateCombo from './PrintParts/DateCombo';
import { ITask, PrintTaskParts } from 'store/tasks/tasksType';

type PrintPartGenerator = {
  [key: string]: React.FC<any>;
};

export const PrintPartSelector: PrintPartGenerator = {
  title: TextPart,
  parentTitle: TextPart,
  AncestorTitle: TextPart,
  fields: FieldPart,
  dueDate: DatePart,
  weight: WeightPart,
  dateCombo: DateCombo,
  subTasks: SubTasksPart,
  assignee: AssignmentPart,
  verifiers: AssignmentPart,
  taskPublisher: AssignmentPart,
  subTaskPublisher: AssignmentPart,
  followers: AssignmentPart,
  projectId: ProjectNamePart,
  startDate: DatePart,
  signatures: SignaturePart,
  subTasksTitle: SubTasksTitlePart,
  mainProgress: MainProgressPart,
  secondaryProgress: MainProgressPart,
  relatedProviderItemId: ProviderItemNamePart,
};

const PartGenerators = (part: PrintTaskParts) => {
  return PrintPartSelector[part];
};

interface IComponentProps {
  printTemplate: ITaskPrintTemplateObject;
}

const PrintSingleTask: React.FC<IComponentProps> = ({
  printTemplate: { header, body },
}) => {
  const headerItemsCount = header.length;
  let headerSize: IPrintTaskHeaderProps['size'];
  if (headerItemsCount < 8) {
    headerSize = 'small';
  } else if (headerItemsCount >= 8 && headerItemsCount < 14) {
    headerSize = 'medium';
  } else {
    headerSize = 'large';
  }
  return (
    <PrintSingleTaskConsumer>
      {(dataContext: IPrintSingleTaskContext) => {
        const { allTasks, taskIds, company } = dataContext;
        if (!allTasks) {
          return <p>Data Changed</p>;
        }
        const icon = company?.documents?.find(
          (it) => it.properties.displayName.toLowerCase() === 'logo'
        )?.url;
        const icon2 = company?.documents?.find(
          (it) => it.properties.displayName.toLowerCase() === 'logo2'
        )?.url;
        return (
          <PrintSingleTaskContainer>
            <table>
              <PrintHeader>
                <tr>
                  <td style={{ padding: 0 }}>
                    <PrintSingleTaskHeaderWrapper>
                      {icon && <LogoImage src={icon} border="left" />}
                      <PrintSingleTaskHeader size={headerSize}>
                        {header.map((item, index) => {
                          console.log(item.part);
                          const HeaderComponent = PartGenerators(item.part);
                          const task = allTasks.find(
                            (t) => t.id === taskIds![item.ref]
                          ) as ITask;
                          const property =
                            task?.[
                              item?.part === 'relatedProviderItemId'
                                ? 'relatedProviderItems'
                                : item?.part
                            ];
                          return (
                            <Fragment
                              key={`${item.part}@${item.ref}[header_${index}]`}
                            >
                              <HeaderComponent
                                item={item}
                                taskId={task?.id}
                                property={property}
                                task={task}
                              />
                              {item.options.newLine && <BreakPart />}
                            </Fragment>
                          );
                        })}
                      </PrintSingleTaskHeader>
                      {icon2 && <LogoImage src={icon2} border="right" />}
                    </PrintSingleTaskHeaderWrapper>

                    <SplitLine />
                  </td>
                </tr>
              </PrintHeader>
              <tbody>
                <>
                  {body.map((item, index) => {
                    if (
                      item.part !== 'subTasks' &&
                      item.part !== 'signatures'
                    ) {
                      const BodyComponent = PartGenerators(item.part);
                      const task = allTasks.find(
                        (t) => t.id === taskIds![item.ref]
                      ) as ITask;
                      const property =
                        task?.[
                          item?.part === 'relatedProviderItemId'
                            ? 'relatedProviderItems'
                            : item?.part
                        ];
                      return (
                        <tr key={`${item.part}@${item.ref}[body${index}]`}>
                          <TD>
                            <BodyComponent
                              item={item}
                              taskId={task?.id}
                              property={property}
                            />
                            {item.options.newLine && <BreakPart />}
                          </TD>
                        </tr>
                      );
                    }
                    if (item.part === 'subTasks') {
                      const SubTasksComponent = PartGenerators(item.part);
                      return (
                        <tr key={`${item.part}@${item.ref}[subTasks${index}]`}>
                          <TD>
                            <SubTasksComponent
                              item={item}
                              taskId={-1}
                              property={allTasks.filter(
                                (t) => t.parentId === taskIds![item.ref]
                              )}
                            />
                            {item.options.newLine && <BreakPart />}
                          </TD>
                        </tr>
                      );
                    }
                    if (item.part === 'signatures') {
                      // render members signatures
                      const SignaturesComponent = PartGenerators(item.part);
                      return (
                        <tr
                          key={`${item.part}@${item.ref}[signatures${index}]`}
                          style={{ pageBreakInside: 'avoid' }}
                        >
                          <TD>
                            <SignaturesComponent
                              item={item}
                              taskId={-1}
                              property={6}
                            />
                            {item.options.newLine && <BreakPart />}
                          </TD>
                        </tr>
                      );
                    }
                    return <></>;
                  })}
                </>
              </tbody>
            </table>
          </PrintSingleTaskContainer>
        );
      }}
    </PrintSingleTaskConsumer>
  );
};

export default memo(PrintSingleTask);
